// External Dependencies
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { NgbModule, NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { JwtModule } from '@auth0/angular-jwt';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { QRCodeModule } from 'angularx-qrcode';
// Routing & Configuration
import { AppRoutingModule } from './app-routing.module';
import { environment } from '../environments/environment';
// Feature Modules
import { SharedModule } from './shared/shared.module';
import { ClubModule } from './modules/club/club.module';
import { UserModule } from './modules/user/user.module';
import { LoyaltyModule } from './modules/loyalty/loyalty.module';
import { VouchersModule } from './modules/vouchers/vouchers.module';
import { EcashModule } from './modules/ecash/ecash.module';
import { PurchaseModule } from './modules/purchase/purchase.module';
import { GuardianModule } from './modules/guardian/guardian.module';
import { EventsModule } from './modules/events/events.module';
import { TicketsModule } from './modules/tickets/tickets.module';
import { FantasyModule } from './modules/fantasy/fantasy.module';
import { PassesModule } from './modules/passes/passes.module';
import { DisplayModule } from './modules/display/display.module';

// Utilities
import { MomentDateFormatter } from './shared/utilities/moment-date-formatter';
// @fgb/core
import {
  AuthGuard,
  PopulationGuard,
  ProductCodeGuard,
  jwtTokenGetter,
  AuthService,
  MemberQuery,
  TMOauthService,
  TMAuthService,
  tmAuthConfig,
  FGBTranslationLoader,
  tmOAuthConfig,
  FGBMissingTranslationHandler,
  MaintenanceGuard,
  DATE_OPTIONS,
  REGISTRATION_ROUTES,
  provideAppConfig,
  provideTMAuth,
} from '@fgb/core';
import { PreviewModule } from '@fgb/portal-component-library/src/lib/preview';
// General Pages
import { AppComponent } from './app.component';
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { ContactUsPageComponent } from './pages/contact-us-page/contact-us-page.component';
import { ReferAFriendPageComponent } from './pages/refer-a-friend-page/refer-a-friend-page.component';
import { ReferAFriendSuccessPageComponent } from './pages/refer-a-friend-page/refer-a-friend-success-page/refer-a-friend-success-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
// Loyalty Pages
import { RewardsLayoutComponent } from './pages/rewards-layout/rewards-layout.component';
import { RewardsPageComponent } from './pages/rewards-page/rewards-page.component';
import { AuctionsPageComponent } from './pages/auctions-page/auctions-page.component';
import { LottosPageComponent } from './pages/lottos-page/lottos-page.component';
import { MarketplacePageComponent } from './pages/marketplace-page/marketplace-page.component';
import { EventsPageComponent } from './pages/events-page/events-page.component';
import { WalletPageComponent } from './pages/wallet-page/wallet-page.component';
import { AuctionDetailPageComponent } from './pages/auction-detail-page/auction-detail-page.component';
import { LottoDetailPageComponent } from './pages/lotto-detail-page/lotto-detail-page.component';
import { MarketplaceDetailPageComponent } from './pages/marketplace-detail-page/marketplace-detail-page.component';
import { LottosSuccessPageComponent } from './pages/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { AuctionBidsPageComponent } from './pages/auction-bids-page/auction-bids-page.component';
import { AuctionBidDetailsPageComponent } from './pages/auction-bid-details-page/auction-bid-details-page.component';
import { LottoWalletDetailsPageComponent } from './pages/lotto-wallet-details-page/lotto-wallet-details-page.component';
import { QuizPageComponent } from './pages/quiz-page/quiz-page.component';
// Polls, Surveys & Trivia (Games)
import { GamesLayoutComponent } from './pages/games-layout/games-layout.component';
import { GamesPageComponent } from './pages/games-page/games-page.component';
import { PollsPageComponent } from './pages/polls-page/polls-page.component';
import { FantasyGamesPageComponent } from './pages/fantasy-games-page/fantasy-games-page.component';
import { FantasyPlayerSelectPageComponent } from './pages/fantasy-player-select-page/fantasy-player-select-page.component';
import { PredictorPageComponent } from './pages/predictor-page/predictor-page.component';
// Account Pages
import { AccountDetailsPageComponent } from './pages/account-details-page/account-details-page.component';
import { ActivityPageComponent } from './pages/activity-page/activity-page.component';
// Ecash Pages
import { EcashPageComponent } from './pages/ecash-page/ecash-page.component';
import { EcashResultPageComponent } from './pages/ecash-page/ecash-result-page/ecash-result-page.component';
import { EcashSuccessPageComponent } from './pages/ecash-page/ecash-success-page/ecash-success-page.component';
import { EcashFailurePageComponent } from './pages/ecash-page/ecash-failure-page/ecash-failure-page.component';
import { EcashHistoryPageComponent } from './pages/ecash-history/ecash-history.component';
// Tickets Pages
import { TicketsPageComponent } from './pages/ticket-transfer-page/tickets-page/tickets-page.component';
import { ManageTicketsPageComponent } from './pages/manage-tickets-page/manage-tickets-page.component';
import { TicketTransferPageComponent } from './pages/ticket-transfer-page/ticket-transfer-page.component';
import { TransferConfirmPageComponent } from './pages/ticket-transfer-page/transfer-confirm-page/transfer-confirm-page.component';
import { TicketTransferSuccessPageComponent } from './pages/ticket-transfer-page/ticket-transfer-success-page/ticket-transfer-success-page.component';
import { ContactsPageComponent } from './pages/contacts-page/contacts-page.component';
import { ManageTicketHistoryPageComponent } from './pages/manage-ticket-history-page/manage-ticket-history-page.component';
// Companies & Departments Pages
import { CompanyPageComponent } from './pages/company-page/company-page.component';
import { CompanyDepartmentsPageComponent } from './pages/company-departments-page/company-departments-page.component';
// Offer Pages
import { OffersPageComponent } from './pages/offers-page/offers-page.component';
import { OfferDetailsPageComponent } from './pages/offer-details-page/offer-details-page.component';
// Population Pages
import { TestPopHomePageComponent } from './pages/test-pop-home-page/test-pop-home-page.component';
// Portal Products
import { PortalProductResultsPageComponent } from './pages/portal-products-pages/portal-product-results-page/portal-product-results-page.component';
import { PortalProductSuccessPageComponent } from './pages/portal-products-pages/portal-product-success-page/portal-product-success-page.component';
import { PortalProductFailurePageComponent } from './pages/portal-products-pages/portal-product-failure-page/portal-product-failure-page.component';
// Benefits
import { BenefitsPageComponent } from './pages/benefits-page/benefits-page.component';
import { BenefitModule } from './modules/benefit/benefit.module';
// External Cash Transfer
import { CardPageComponent } from './pages/card-page/card-page.component';
import { TransferArenaCashPageComponent } from './pages/transfer-arena-cash-page/transfer-arena-cash-page.component';
import { TransferMultipleAccountsPageComponent } from './pages/transfer-multiple-accounts-page/transfer-multiple-accounts-page.component';
import { TransferArenaCashSuccessPageComponent } from './pages/transfer-arena-cash-success-page/transfer-arena-cash-success-page.component';
// Club Pages
import { EarnPageComponent } from './pages/earn-page/earn-page.component';
import { GuardianPageComponent } from './pages/guardian-page/guardian-page.component';
import { LayoutModule } from './modules/layouts/layout.module';
import { InlineSVGModule } from 'ng-inline-svg-2';
// Points Management Page
import { PointsManagementPageComponent } from './pages/points-management-page/points-management-page.component';

// Partners Pages
import { PartnerPageComponent } from './pages/partner-page/partner-page.component';
import { LeaderboardPageComponent } from './pages/leaderboard-page/leaderboard-page.component';
import { LottosTermsPageComponent } from './pages/lottos-terms-page/lottos-terms-page.component';
import { MarketplaceTermsPageComponent } from './pages/marketplace-terms-page/marketplace-terms-page.component';
import { JokesPageComponent } from './pages/jokes-page/jokes-page.component';
import { WorksheetPageComponent } from './pages/worksheet-page/worksheet-page.component';
import { ZoomMeetingPageComponent } from './pages/zoom-meeting-page/zoom-meeting-page.component';
import { FantasyLeaderboardPageComponent } from './pages/fantasy-leaderboard-page/fantasy-leaderboard-page.component';
import { CarouselModule } from 'ngx-owl-carousel-o';
import { RenewalBonusTransferPageComponent } from './pages/renewal-bonus-transfer-page/renewal-bonus-transfer-page.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { CartCheckoutPagePortalProductsComponent } from './pages/cart-checkout-page-portal-products/cart-checkout-page-portal-products.component';
import { CartCheckoutPageComponent } from './pages/cart-checkout-page/cart-checkout-page.component';
import { CartCheckoutResultsPageComponent } from './pages/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { NotificationManagementPageComponent } from './pages/notification-management-page/notification-management-page.component';
import { TailgatePageComponent } from './pages/tailgate-page/tailgate-page.component';
import { ScorePredictorPageComponent } from './pages/score-predictor-page/score-predictor-page.component';
// CL Components
import { BadgesPageComponent } from './pages/badges-page/badges-page.component';

import {
  BadgeAchievedModule,
  BadgeListItemModule,
  BadgeOverviewListModule,
  BadgeFeaturedListModule,
} from '@fgb/portal-component-library/src/lib/loyalty/badges';
import { CampaignsModule } from '@fgb/portal-component-library/src/lib/campaigns';
import { AnnouncementsModule } from '@fgb/portal-component-library/src/lib/club/announcements';
import { EarnPointsModule } from '@fgb/portal-component-library/src/lib/club/earn-points';
import { MarketplaceListModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-list';
import { MarketplaceDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/marketplace/marketplace-details';
import { AuctionListModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-list';
import { AuctionDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/auctions/auction-details';
import { LottosListModule } from '@fgb/portal-component-library/src/lib/loyalty/lottos/lottos-list';
import { LottoDetailsModule } from '@fgb/portal-component-library/src/lib/loyalty/lottos/lotto-details';
import { CardTokenListModule } from '@fgb/portal-component-library/src/lib/ecash/card-token-list';
import { ClaimedPageComponent } from './pages/claimed-page/claimed-page.component';
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';
import { FeaturedRewardsModule } from '@fgb/portal-component-library/src/lib/loyalty/featured-rewards';
import { WalletModule } from '@fgb/portal-component-library/src/lib/loyalty/wallet';
import { NotificationModule } from '@fgb/portal-component-library/src/lib/club/notification';
import { mergeRegistrationRoutes } from '@fgb/portal-component-library/src/lib/registration';
import { ScorecardModule } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { FaqSectionModule } from '@fgb/portal-component-library/src/lib/club/faq-section';
import { TermsAndConditionsModule } from '@fgb/portal-component-library/src/lib/club/terms-and-conditions';
import { MixedLoyaltyListModule } from '@fgb/portal-component-library/src/lib/loyalty/mixed-loyalty';
import { CartItemModule } from '@fgb/portal-component-library/src/lib/loyalty/cart-item';
import { BadgesNavComponent } from './pages/badges-nav/badges-nav.component';
import { CardLinkModule } from '@fgb/portal-component-library/src/lib/cardlinking';
import { FilterModalComponent } from './pages/rewards-page/filter-modal/filter-modal.component';
import { BannerComponent } from './pages/banner/banner.component';
import { ErrorsModule } from '@fgb/portal-component-library/src/lib/shared/errors';
import { CountryAndRegionsModule } from '@fgb/portal-component-library/src/lib/shared/country-and-regions';
import { DirectivesModule } from '@fgb/portal-component-library/src/lib/directives';
import { WalletClaimedPageComponent } from './pages/wallet-claimed-page/wallet-claimed-page.component';
import { CampaignDetailsPageComponent } from './pages/campaign-details-page/campaign-details-page.component';
import { MarketplaceWalletPageComponent } from './pages/marketplace-wallet-page/marketplace-wallet-page.component';
import { AuctionWalletDetailsPageComponent } from './pages/auction-wallet-details-page/auction-wallet-details-page.component';
const tmSSOAuthConfig: tmOAuthConfig = {
  clientId: '603ad9a4785f.fortress.nyjets-jets.us',
  club: 'nyjets',
  clientSecret: 'ee89f796cb443e733729ea5cefc3cc50f942ba324b6433c2d9c9f743ff80c4c7',
  tokenUri: 'https://auth.ticketmaster.com/api/oauth/token',
  loginUri: 'https://am.ticketmaster.com/nyjets/sso',
  authoriseUri: 'https://auth.ticketmaster.com/as/authorization.oauth2',
};

const tmLoginAuthConfig: tmAuthConfig = {
  clientId: '603ad9a4785f.fortress.nyjets-jets.us',
  authUri: 'https://auth.ticketmaster.com/as/authorization.oauth2',
  loginUri: 'https://am.ticketmaster.com/nyjets/sso',
  visualPresents: 'nyjets',
};

@NgModule({
  declarations: [
    // General Pages
    AppComponent,
    MasterPageComponent,
    HomePageComponent,
    LoginPageComponent,
    LoginAccountsPageComponent,
    MaintenancePageComponent,
    TermsPageComponent,
    FaqPageComponent,
    ContactUsPageComponent,
    ReferAFriendPageComponent,
    ReferAFriendSuccessPageComponent,
    NotFoundPageComponent,
    // Loyalty Pages
    RewardsLayoutComponent,
    RewardsPageComponent,
    AuctionsPageComponent,
    LottosPageComponent,
    MarketplacePageComponent,
    EventsPageComponent,
    WalletPageComponent,
    AuctionDetailPageComponent,
    LottoDetailPageComponent,
    MarketplaceDetailPageComponent,
    LottosSuccessPageComponent,
    MarketplacePurchaseSuccessPageComponent,
    AuctionBidsPageComponent,
    AuctionBidDetailsPageComponent,
    LottoWalletDetailsPageComponent,
    AuctionWalletDetailsPageComponent,
    // Polls, Surveys & Trivia Pages (Games)
    GamesLayoutComponent,
    GamesPageComponent,
    PollsPageComponent,
    FantasyGamesPageComponent,
    FantasyPlayerSelectPageComponent,
    PredictorPageComponent,
    // Account Pages
    AccountDetailsPageComponent,
    ActivityPageComponent,
    PointsManagementPageComponent,
    // Ecash Pages
    EcashPageComponent,
    EcashResultPageComponent,
    EcashSuccessPageComponent,
    EcashFailurePageComponent,
    EcashHistoryPageComponent,
    PortalProductResultsPageComponent,
    PortalProductSuccessPageComponent,
    PortalProductFailurePageComponent,
    // Tickets Pages
    TicketsPageComponent,
    ManageTicketsPageComponent,
    TicketTransferPageComponent,
    TransferConfirmPageComponent,
    TicketTransferSuccessPageComponent,
    ContactsPageComponent,
    ManageTicketHistoryPageComponent,
    // Companies & Departments Pages
    CompanyPageComponent,
    CompanyDepartmentsPageComponent,
    // Offer Pages
    OffersPageComponent,
    OfferDetailsPageComponent,
    // Population Pages
    TestPopHomePageComponent,
    // Guardians Pages
    GuardianPageComponent,
    // External Cash Transfers
    CardPageComponent,
    TransferArenaCashPageComponent,
    TransferMultipleAccountsPageComponent,
    TransferArenaCashSuccessPageComponent,
    // Club Pages
    EarnPageComponent,
    BenefitsPageComponent,
    QuizPageComponent,
    PartnerPageComponent,
    LeaderboardPageComponent,
    LottosTermsPageComponent,
    MarketplaceTermsPageComponent,
    JokesPageComponent,
    WorksheetPageComponent,
    ZoomMeetingPageComponent,
    FantasyLeaderboardPageComponent,
    RenewalBonusTransferPageComponent,
    CartCheckoutPagePortalProductsComponent,
    CartCheckoutPageComponent,
    CartCheckoutSuccessPageComponent,
    CartCheckoutResultsPageComponent,
    NotificationManagementPageComponent,
    TailgatePageComponent,
    ScorePredictorPageComponent,
    BadgesPageComponent,
    ClaimedPageComponent,
    RegistrationLayoutPageComponent,
    BadgesNavComponent,
    FilterModalComponent,
    WalletClaimedPageComponent,
    CampaignDetailsPageComponent,
    BannerComponent,
    MarketplaceWalletPageComponent,
  ],
  imports: [
    // External Modules
    RouterModule,
    CarouselModule,
    AppRoutingModule,
    FormsModule,
    NgbModule,
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    InlineSVGModule.forRoot(),
    QRCodeModule,
    NotificationModule,
    BrowserAnimationsModule,
    CardLinkModule,
    ToastrModule.forRoot({
      maxOpened: 5,
    }),
    JwtModule.forRoot({
      config: {
        allowedDomains: ['http://localhost/portal2/api'],
        tokenGetter: jwtTokenGetter,
      },
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: FGBTranslationLoader,
        deps: [HttpClient, AuthService, MemberQuery],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: FGBMissingTranslationHandler,
      },
    }),
    environment.production ? [] : [],
    // Feature Modules
    LayoutModule,
    GuardianModule,
    PurchaseModule,
    EventsModule,
    ClubModule,
    EcashModule,
    PurchaseModule,
    VouchersModule,
    UserModule,
    SharedModule,
    FantasyModule,
    TicketsModule,
    LoyaltyModule,
    BenefitModule,
    PassesModule,
    DisplayModule,
    BadgeListItemModule,
    BadgeAchievedModule,
    BadgeOverviewListModule,
    BadgeFeaturedListModule,
    EarnPointsModule,
    MarketplaceListModule,
    MarketplaceDetailsModule,
    AuctionDetailsModule,
    AuctionListModule,
    LottoDetailsModule,
    LottosListModule,
    CardTokenListModule,
    FeaturedRewardsModule,
    WalletModule,
    ScorecardModule,
    FaqSectionModule,
    TermsAndConditionsModule,
    CardLinkModule,
    MixedLoyaltyListModule,
    CartItemModule,
    PreviewModule,
    ErrorsModule,
    CountryAndRegionsModule,
    CampaignsModule,
    AnnouncementsModule,
    DirectivesModule,
  ],
  providers: [
    provideAppConfig({
      dateOptions: {
        LongDateFormat: 'MMM D, yyyy h:mma',
      },
    }),
    provideTMAuth({
      ssoAuth: tmSSOAuthConfig,
      loginAuth: tmLoginAuthConfig,
    }),
    {
      provide: DATE_OPTIONS,
      useValue: {
        LongDateFormat: 'MMM DD, yyyy h:mma',
        ShortDateFormat: 'MMM DD, yyyy',
      },
    },
    {
      provide: REGISTRATION_ROUTES,
      useFactory: () => mergeRegistrationRoutes(),
    },
    TMOauthService,
    TMAuthService,
    AuthGuard,
    MaintenanceGuard,
    AuthService,
    PopulationGuard,
    ProductCodeGuard,
    DatePipe,
    CurrencyPipe,
    CookieService,
    { provide: NgbDateParserFormatter, useClass: MomentDateFormatter },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
